// src/pages/CategoryPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FaImage, FaEdit, FaTrashAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';

export default function CategoryPage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const fileInputRef = useRef(null);

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    
    fetchCategories();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCategories = async () => {
    setIsFetching(true);
    try {
      const requestOptions = {
        method: "GET",
        redirect: "follow"
      };

      const response = await fetch("https://api.alburaqscents.com/v1/category", requestOptions);
      const result = await response.json();

      if (result.success) {
        const transformedCategories = result.categories.map(category => ({
          id: category._id,
          name: category.name,
          image: category.image,
          items: 0
        }));
        setCategories(transformedCategories);
      } else {
        toast.error(result.message || 'Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('An error occurred while fetching categories');
    } finally {
      setIsFetching(false);
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload a PNG, JPG, or WEBP image.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size too large. Maximum size is 5MB.');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImagePreview(null);
    setName('');
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddCategory = async () => {
    if (!name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    if (!imageFile && !editingId) {
      toast.error('Please upload an image for the category');
      return;
    }

    setIsLoading(true);

    try {
      const formdata = new FormData();
      formdata.append("name", name);
      if (imageFile) {
        formdata.append("image", imageFile);
      }

      const requestOptions = {
        method: editingId ? "PUT" : "POST",
        body: formdata,
        redirect: "follow"
      };

      const endpoint = editingId 
        ? `https://api.alburaqscents.com/v1/category/${editingId}`
        : "https://api.alburaqscents.com/v1/category";

      const response = await fetch(endpoint, requestOptions);
      const result = await response.json();

      if (result.success) {
        toast.success(`Category ${editingId ? 'updated' : 'created'} successfully!`);
        await fetchCategories();
        resetForm();
      } else {
        toast.error(result.message || `Failed to ${editingId ? 'update' : 'create'} category`);
      }
    } catch (error) {
      console.error(`Error ${editingId ? 'updating' : 'creating'} category:`, error);
      toast.error(`An error occurred while ${editingId ? 'updating' : 'creating'} the category`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (id) => {
    const categoryToEdit = categories.find(cat => cat.id === id);
    if (categoryToEdit) {
      setName(categoryToEdit.name);
      setImagePreview(categoryToEdit.image);
      setEditingId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const confirmDelete = (id) => {
    setCategoryToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    try {
      setIsUpdating(true);
      const requestOptions = {
        method: "DELETE",
        redirect: "follow"
      };

      const response = await fetch(`https://api.alburaqscents.com/v1/category/${categoryToDelete}`, requestOptions);
      const result = await response.json();

      if (result.success) {
        toast.success('Category deleted successfully');
        setCategories(categories.filter(cat => cat.id !== categoryToDelete));
      } else {
        toast.error(result.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setIsUpdating(false);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const cancelEdit = () => {
    resetForm();
  };

  const styles = {
    mainWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: 'transparent',
      padding: isSmallMobile ? '0' : (isMobile ? '15px' : '20px'),
      boxSizing: 'border-box',
      fontFamily: 'Inter, sans-serif',
      overflowY: 'auto',
      position: 'relative',
    },
    categoryPageContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '1200px',
      backgroundColor: 'transparent',
      padding: isSmallMobile ? '15px' : '20px',
      boxSizing: 'border-box',
      minHeight: isMobile ? 'calc(100vh - 30px)' : 'calc(100vh - 40px)',
      overflow: 'hidden',
    },
             mainWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: 'transparent',
      padding: isSmallMobile ? '0' : (isMobile ? '15px' : '20px'),
      boxSizing: 'border-box',
      fontFamily: 'Inter, sans-serif',
      overflowY: 'auto',
    },
    categoryPageContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '1200px',
      backgroundColor: 'transparent',
      padding: isSmallMobile ? '15px' : '20px',
      boxSizing: 'border-box',
      minHeight: isMobile ? 'calc(100vh - 30px)' : 'calc(100vh - 40px)',
      overflow: 'hidden',
    },
    headerSection: {
      marginBottom: isSmallMobile ? '15px' : '25px',
      textAlign: 'left',
    },
    mainTitle: {
      fontSize: isSmallMobile ? '18px' : (isMobile ? '20px' : '22px'),
      color: '#000',
      marginBottom: '5px',
      fontWeight: 'bold',
    },
    breadcrumbs: {
      fontSize: isSmallMobile ? '12px' : '14px',
      color: '#555',
    },
    formAndTableLayout: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr',
      gap: isMobile ? '20px' : '30px',
      flexGrow: 1,
      alignItems: 'start',
    },
    formSection: {
      backgroundColor: '#000',
      borderRadius: '10px',
      padding: '25px',
      color: '#E5D0A5',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      boxSizing: 'border-box',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    formLabel: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#E5D0A5',
      marginBottom: '5px',
    },
    imageUploadContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      padding: '20px',
      border: '2px dashed #333',
    },
    imagePreview: {
      width: '100px',
      height: '100px',
      objectFit: 'cover',
      borderRadius: '8px',
      marginBottom: '10px',
    },
    imagePlaceholderIcon: {
      fontSize: '60px',
      color: '#E5D0A5',
      marginBottom: '10px',
    },
    uploadImageText: {
      fontSize: '14px',
      color: '#888',
      marginBottom: '10px',
      textAlign: 'center',
    },
    uploadButton: {
      backgroundColor: '#E5D0A5',
      color: '#000',
      border: 'none',
      borderRadius: '5px',
      padding: '10px 20px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
    },
    fileTypeInfo: {
      fontSize: '11px',
      color: '#888',
      marginTop: '10px',
      textAlign: 'center',
    },
    inputField: {
      width: '100%',
      padding: '12px',
      borderRadius: '5px',
      border: '1px solid #333',
      backgroundColor: '#1a1a1a',
      color: '#E5D0A5',
      fontSize: '14px',
      boxSizing: 'border-box',
    },
    addCategoryButton: {
      backgroundColor: '#E5D0A5',
      color: '#000',
      border: 'none',
      borderRadius: '5px',
      padding: '12px 25px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      alignSelf: 'flex-start',
      marginTop: '10px',
      opacity: isLoading ? 0.7 : 1,
      pointerEvents: isLoading ? 'none' : 'auto',
    },
    tableSection: {
      backgroundColor: '#000',
      borderRadius: '10px',
      padding: '25px',
      color: '#E5D0A5',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    },
    tableWrapper: {
      flexGrow: 1,
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      tableLayout: 'auto',
      minWidth: '100%',
    },
    tableHeader: {
      backgroundColor: '#000',
      color: '#E5D0A5',
      fontSize: '12px',
      textAlign: 'left',
      padding: '8px 0',
      borderBottom: '1px solid #333',
    },
    th: {
      padding: '16px 10px',
      whiteSpace: 'nowrap',
    },
    tableRow: {
      borderBottom: '1px solid #333',
    },
    tableRowHover: {
      backgroundColor: '#1a1a1a',
    },
    td: {
      padding: '16px 10px',
      fontSize: '12px',
      whiteSpace: 'nowrap',
    },
    categoryIcon: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#E5D0A5',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#000',
      fontSize: '10px',
      fontWeight: 'bold',
      marginRight: '5px',
      flexShrink: 0,
    },
    categoryImage: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      objectFit: 'cover',
      marginRight: '5px',
    },
    categoryNameContainer: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
    },
    itemsCount: {
      color: '#E5D0A5',
      fontWeight: 'bold',
      fontSize: '12px',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    actionButton: {
      backgroundColor: '#E5D0A5',
      border: 'none',
      borderRadius: '5px',
      padding: '6px 10px',
      cursor: 'pointer',
      fontSize: '12px',
      color: '#000',
    },
    actionButtonIcon: {
      fontSize: '14px',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
      width: '100%',
    },
    spinner: {
      animation: 'spin 1s linear infinite',
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
    formHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
    },
    cancelEditButton: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#E5D0A5',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    deleteModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    },
    deleteModalContent: {
      backgroundColor: '#000',
      borderRadius: '10px',
      padding: '25px',
      color: '#E5D0A5',
      width: isSmallMobile ? '90%' : (isMobile ? '70%' : '400px'),
      boxSizing: 'border-box',
      border: '2px solid #E5D0A5',
    },
    deleteModalTitle: {
      fontSize: '17px',
      fontWeight: 'bold',
      marginBottom: '20px',
      color: '#E5D0A5',
    },
    deleteModalMessage: {
      marginBottom: '25px',
      fontSize: '16px',
    },
    deleteModalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '15px',
    },
    deleteModalButton: {
      padding: '10px 20px',
      borderRadius: '5px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
    },
    deleteModalCancel: {
      backgroundColor: 'transparent',
      color: '#E5D0A5',
      border: '1px solid #E5D0A5',
    },
    deleteModalConfirm: {
      backgroundColor: '#E5D0A5',
      color: '#000',
    },
   
  };

  return (
    <div style={styles.mainWrapper}>
      <Toaster position="top-right" />
      <div style={styles.categoryPageContainer}>
        <div style={styles.headerSection}>
          <h1 style={styles.mainTitle}>Category</h1>
          <p style={styles.breadcrumbs}>Home • Category List</p>
        </div>

        <div style={styles.formAndTableLayout}>
          <div style={styles.formSection}>
            <div style={styles.formHeader}>
              <h2 style={{ color: '#E5D0A5', margin: 0 }}>
                {editingId ? 'Edit Category' : 'Add New Category'}
              </h2>
              {editingId && (
                <button style={styles.cancelEditButton} onClick={cancelEdit}>
                  <FaTimes /> Cancel
                </button>
              )}
            </div>

            <div style={styles.formGroup}>
              <p style={styles.formLabel}>Upload Image</p>
              <div style={styles.imageUploadContainer}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" style={styles.imagePreview} />
                ) : (
                  <FaImage style={styles.imagePlaceholderIcon} />
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".png,.jpg,.jpeg,.webp"
                />
                <button
                  style={styles.uploadButton}
                  onClick={() => fileInputRef.current.click()}
                >
                  Choose File
                </button>
                <p style={styles.fileTypeInfo}>(Only png, jpg, jpeg, webp allowed)</p>
              </div>
            </div>

            <div style={styles.formGroup}>
              <p style={styles.formLabel}>Name</p>
              <input
                type="text"
                style={styles.inputField}
                placeholder="Category Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <button
              style={styles.addCategoryButton}
              onClick={handleAddCategory}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                  {editingId ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                editingId ? 'Update Category' : 'Add Category'
              )}
            </button>
          </div>

          <div style={styles.tableSection}>
            <div style={styles.tableWrapper}>
              {isFetching ? (
                <div style={styles.loadingContainer}>
                  <FaSpinner style={{ fontSize: '17px', animation: 'spin 1s linear infinite' }} />
                </div>
              ) : categories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#E5D0A5' }}>
                  No categories found
                </div>
              ) : (
                <table style={styles.table}>
                  <thead style={styles.tableHeader}>
                    <tr>
                      <th style={styles.th}>NAME</th>
                      <th style={styles.th}>ITEMS</th>
                      <th style={styles.th}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr
                        key={category.id}
                        style={{
                          ...styles.tableRow,
                          backgroundColor: editingId === category.id ? '#2a2a2a' : 'transparent'
                        }}
                        onMouseOver={(e) => editingId !== category.id && (e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor)}
                        onMouseOut={(e) => editingId !== category.id && (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <td style={styles.td}>
                          <div style={styles.categoryNameContainer}>
                            {category.image ? (
                              <img 
                                src={category.image} 
                                alt={category.name} 
                                style={styles.categoryImage} 
                              />
                            ) : (
                              <div style={styles.categoryIcon}>
                                {category.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            {category.name}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.itemsCount}>{category.items}</span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.actionButton}
                              onClick={() => handleEditCategory(category.id)}
                              disabled={isUpdating}
                            >
                              <FaEdit style={styles.actionButtonIcon} />
                            </button>
                            <button
                              style={styles.actionButton}
                              onClick={() => confirmDelete(category.id)}
                              disabled={isUpdating}
                            >
                              <FaTrashAlt style={styles.actionButtonIcon} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.deleteModal}>
          <div style={styles.deleteModalContent}>
            <h3 style={styles.deleteModalTitle}>Delete Category</h3>
            <p style={styles.deleteModalMessage}>
              Are you sure you want to delete this category? This action cannot be undone.
            </p>
            <div style={styles.deleteModalButtons}>
              <button 
                style={{ ...styles.deleteModalButton, ...styles.deleteModalCancel }}
                onClick={cancelDelete}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button 
                style={{ ...styles.deleteModalButton, ...styles.deleteModalConfirm }}
                onClick={handleDeleteCategory}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}